import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Package, Users, DollarSign, TrendingUp, Clock } from "lucide-react";
import { StatCard } from "./shared/StatCard";
import { useLanguage } from "../contexts/LanguageContext";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./AdminDashboard.css";
import cacheStorage from '../utils/cacheStorage';

const dailyData = [
  { date: "Mar 18", shipments: 145 },
  { date: "Mar 19", shipments: 168 },
  { date: "Mar 20", shipments: 152 },
  { date: "Mar 21", shipments: 189 },
  { date: "Mar 22", shipments: 201 },
  { date: "Mar 23", shipments: 176 },
  { date: "Mar 24", shipments: 195 },
];

const weeklyData = [
  { date: "Week 1", shipments: 1045 },
  { date: "Week 2", shipments: 1168 },
  { date: "Week 3", shipments: 1252 },
  { date: "Week 4", shipments: 1389 },
];

const monthlyData = [
  { date: "Oct", shipments: 4245 },
  { date: "Nov", shipments: 4568 },
  { date: "Dec", shipments: 5052 },
  { date: "Jan", shipments: 4789 },
  { date: "Feb", shipments: 5198 },
  { date: "Mar", shipments: 5426 },
];

const recentActivity = [
  { text: "New shipment #PKG-2891 created", time: "2 minutes ago" },
  { text: "Payment of ₼1,245 received", time: "15 minutes ago" },
  { text: "Courier David Lee registered", time: "32 minutes ago" },
  { text: "Dispute #DSP-445 reported", time: "1 hour ago" },
  { text: "Shipment #PKG-2889 delivered", time: "2 hours ago" },
  { text: "New warehouse added in Istanbul", time: "3 hours ago" },
];

export default function AdminDashboard() {
  const [viewMode, setViewMode] = useState("daily");
  const [topCouriers, setTopCouriers] = useState([]);
  const [stats, setStats] = useState({
    totalShipments: null,  // null = still loading
    activeCouriers: null,
    error: false,
  });

  const navigate = useNavigate();
  const { t } = useLanguage();
  const userName = cacheStorage.getItem('user_name') || 'Admin';

  // Fetch live stats
  useEffect(() => {
    const token = cacheStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/stats`, { headers })
        .then(res => {
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          return res.json();
        })
        .then(data => {
          setStats({
            totalShipments: data.total_shipments,
            activeCouriers: data.active_couriers,
            error: false,
          });
        })
        .catch(err => {
          console.error("Failed to load stats:", err);
          setStats(prev => ({ ...prev, error: true }));
        });
  }, []);

  // Fetch top couriers
  useEffect(() => {
    const token = cacheStorage.getItem('access_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};

    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/top-couriers`, { headers })
        .then(res => res.json())
        .then(data => setTopCouriers(data || []))
        .catch(err => console.error("Failed to load top couriers:", err));
  }, []);

  const getChartData = () => {
    switch (viewMode) {
      case "daily":   return dailyData;
      case "weekly":  return weeklyData;
      case "monthly": return monthlyData;
      default:        return dailyData;
    }
  };

  const getAvgPackages = () => {
    const data = getChartData();
    if (!data || data.length === 0) return 0;
    const total = data.reduce((sum, item) => sum + item.shipments, 0);
    return Math.round(total / data.length);
  };

  // Display helpers: "..." while loading, "—" on error, formatted number otherwise
  const fmt = (val) => {
    if (stats.error) return "—";
    if (val === null) return "...";
    return val.toLocaleString();
  };

  return (
      <div className="admin-dashboard">
        <div className="header-section">
          <h1 className="title">Welcome, {userName}!</h1>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
        .search-result-item:hover {
          background-color: #F3F4F6;
        }
      `}} />

        <div className="stats-grid">
          <StatCard
              title={t.totalShipments}
              value={fmt(stats.totalShipments)}
              icon={Package}
              trend={stats.totalShipments !== null ? `↑ 12% ${t.fromLastWeek}` : ""}
              onClick={() => navigate("/admin/analytics")}
          />
          <StatCard
              title={t.activeCouriers}
              value={fmt(stats.activeCouriers)}
              icon={Users}
              trend={stats.activeCouriers !== null ? `↑ 5 ${t.newThisWeek}` : ""}
              onClick={() => navigate("/admin/analytics")}
          />
          <StatCard
              title={t.revenue}
              value="₼48.2K"
              icon={DollarSign}
              trend={`↑ 18% ${t.fromLastMonth}`}
              onClick={() => navigate("/admin/analytics")}
          />
          <StatCard
              title={t.completionRate}
              value="94.8%"
              icon={TrendingUp}
              trend={`↑ 2.3% ${t.improvement}`}
              onClick={() => navigate("/admin/analytics")}
          />
        </div>

        <div className="main-content-grid">
          <div className="chart-card">
            <div className="chart-header">
              <h2 className="section-title">{t.shipmentTrends}</h2>
              <div className="view-mode-toggles">
                {["daily", "weekly", "monthly"].map((mode) => (
                    <button
                        key={mode}
                        onClick={() => setViewMode(mode)}
                        className={`toggle-button ${viewMode === mode ? "active" : ""}`}
                    >
                      {t[mode].charAt(0).toUpperCase() + t[mode].slice(1)}
                    </button>
                ))}
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={getChartData()}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" key="grid" />
                  <XAxis dataKey="date" stroke="#708094" key="xaxis" />
                  <YAxis stroke="#708094" key="yaxis" />
                  <Tooltip
                      key="tooltip"
                      contentStyle={{
                        backgroundColor: "#fff",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                      }}
                  />
                  <Line
                      key="line-shipments"
                      type="monotone"
                      dataKey="shipments"
                      stroke="#2D58AD"
                      strokeWidth={3}
                      dot={{ fill: "#2D58AD", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="chart-stats-grid">
              <div className="chart-stat-box success">
                <p className="stat-label">{t.successfulDeliveries}</p>
                <p className="stat-value">92.3%</p>
              </div>
              <div className="chart-stat-box info">
                <p className="stat-label">{t.avgPackagesDelivered}</p>
                <p className="stat-value">{getAvgPackages()}</p>
              </div>
            </div>
          </div>

          <div className="top-couriers-card">
            <h2 className="section-title">{t.topCouriers}</h2>
            <div className="couriers-list">
              {topCouriers.length === 0 ? (
                  <p style={{ color: "#9CA3AF", fontSize: 14 }}>No data yet</p>
              ) : (
                  topCouriers.map((courier, index) => (
                      <div key={courier.id} className="courier-item">
                        <div className="courier-rank">{index + 1}</div>
                        <div className="courier-details">
                          <p className="courier-name">{courier.name}</p>
                          <p className="courier-deliveries">{courier.deliveries} {t.deliveries}</p>
                        </div>
                        <div className="courier-status-dot"></div>
                      </div>
                  ))
              )}
            </div>
          </div>
        </div>

        <div className="recent-activity-card">
          <h2 className="section-title">{t.recentActivity}</h2>
          <div className="activity-list">
            {recentActivity.map((activity, index) => (
                <div key={index} className="activity-item">
                  <div className="activity-dot"></div>
                  <div className="activity-details">
                    <p className="activity-text">{activity.text}</p>
                    <div className="activity-time">
                      <Clock size={12} />
                      <span>{activity.time}</span>
                    </div>
                  </div>
                </div>
            ))}
          </div>
        </div>
      </div>
  );
}